import { useQuery } from "@apollo/client/react";
import {
  createContext,
  createElement,
  use,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  AGENT_PRESENCE_QUERY,
  AGENT_PRESENCE_SUBSCRIPTION,
} from "@/components/agent/presenceQueries";
import { useFeatureEnabled } from "@/providers/ProfileProvider";

/** A colleague working on a ticket's customer answer right now. */
export interface WorkingAgent {
  agentId: string;
  displayName: string;
}

/** Who is working on which ticket, by conversation id. Never the viewer. */
export type PresenceMap = Map<string, WorkingAgent[]>;

const EMPTY_MAP: PresenceMap = new Map();

// Only "working" is shown: an agent who changed the customer answer (draft, facts,
// sources) in the last 30 s. Opening or reading a ticket ("viewing") never shows.
const WORKING = "working";

const PresenceContext = createContext<PresenceMap>(EMPTY_MAP);

function usePresenceFeed(): PresenceMap {
  const enabled = useFeatureEnabled("groupware-presence", false);

  const { data, subscribeToMore } = useQuery(AGENT_PRESENCE_QUERY, {
    skip: !enabled,
    pollInterval: 60_000,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = subscribeToMore({
      document: AGENT_PRESENCE_SUBSCRIPTION,
      updateQuery: (_prev, { subscriptionData }) => {
        const next = subscriptionData.data?.agentPresenceUpdated;
        if (!next) return;
        // The event carries the same fields as the query's entry but is a distinct
        // schema type, so the fields are copied across explicitly instead of the events
        // being passed off as entries. Each event carries the whole presence list, so
        // this replaces rather than merges.
        return {
          agentPresence: next.map(
            ({ agentId, displayName, conversationId, source, activity }) => ({
              agentId,
              displayName,
              conversationId,
              source,
              activity,
            }),
          ),
        };
      },
      onError: (err) =>
        console.warn("agentPresenceUpdated subscription error", err),
    });
    return () => unsubscribe();
  }, [enabled, subscribeToMore]);

  return useMemo(() => {
    if (!enabled || !data?.agentPresence?.length) return EMPTY_MAP;
    const map: PresenceMap = new Map();
    for (const e of data.agentPresence) {
      if (e.activity !== WORKING) continue;
      const arr = map.get(e.conversationId) ?? [];
      if (!arr.some((p) => p.agentId === e.agentId)) {
        arr.push({ agentId: e.agentId, displayName: e.displayName });
      }
      map.set(e.conversationId, arr);
    }
    return map;
  }, [data, enabled]);
}

/** One presence feed for the ticket list, the ticket header and the assistant panel. */
export function AgentPresenceProvider({ children }: { children: ReactNode }) {
  const presence = usePresenceFeed();
  return createElement(PresenceContext.Provider, { value: presence }, children);
}

/** Who is working on which ticket (empty outside an AgentPresenceProvider). */
export function useAgentPresence(): PresenceMap {
  return use(PresenceContext);
}

/** The colleague working on this ticket right now, or null. */
export function useWorkingColleague(
  conversationId: string | null | undefined,
): WorkingAgent | null {
  const presence = useAgentPresence();
  if (!conversationId) return null;
  return presence.get(conversationId)?.[0] ?? null;
}
